// src/api/catalog.ts — Square catalog & inventory read/write functions

import {
  BatchRetrieveCatalogObjectsResponse,
  CatalogObject,
  RetrieveCatalogObjectResponse,
} from "square";
import { ApiResult } from "../types/ApiResult";
import { SquareFilterType } from "../types/SquareFilterType";

// Shared auth header for all catalog proxy calls.
const apiKey = process.env.REACT_APP_FUNCTION_SECRET;
const authHeaders: HeadersInit = apiKey ? { "x-api-key": apiKey } : {};

// ---------------------------------------------------------------------------
// Low-level catalog fetchers
// ---------------------------------------------------------------------------

/** Fetch all catalog objects of the given comma-separated types (e.g. "ITEM,IMAGE"). */
export const getAll = async (
  types: string,
): Promise<ApiResult<CatalogObject[]>> => {
  try {
    const response = await fetch(
      `/.netlify/functions/get-catalog?types=${types}`,
      {
        method: "GET",
        headers: { "Content-Type": "application/json", ...authHeaders },
      },
    );
    return response.json();
  } catch (error) {
    console.error("Error getting catalog:", error);
    return { error: `Error getting catalog: ${error}` };
  }
};

/** Fetch all top-level categories. */
export const getCategories = async (): Promise<ApiResult<CatalogObject[]>> => {
  try {
    const response = await fetch(`/.netlify/functions/get-catalog-categories`, {
      method: "GET",
      headers: { "Content-Type": "application/json", ...authHeaders },
    });
    return response.json();
  } catch (error) {
    console.error("Error getting catalog categories:", error);
    return { error: `Error getting categories: ${error}` };
  }
};

/** Fetch a single catalog object by ID. */
export const getObject = async (
  objectId: string,
): Promise<ApiResult<RetrieveCatalogObjectResponse>> => {
  if (!objectId) return null;
  try {
    const response = await fetch(
      `/.netlify/functions/get-catalog-object?objectId=${objectId}`,
      {
        method: "GET",
        headers: { "Content-Type": "application/json", ...authHeaders },
      },
    );
    return response.json();
  } catch (error) {
    console.error("Error getting catalog object:", error);
    return { error: `Error getting catalog object: ${error}` };
  }
};

/** Fetch all catalog items. */
export const getAllItems = async (): Promise<ApiResult<CatalogObject[]>> => {
  try {
    const response = await fetch(`/.netlify/functions/get-catalog-items`, {
      method: "GET",
      headers: { "Content-Type": "application/json", ...authHeaders },
    });
    return response.json();
  } catch (error) {
    console.error("Error getting catalog items:", error);
    return { error: `Error getting catalog items: ${error}` };
  }
};

/** Fetch all items belonging to a specific category. */
export const getBaseItemsByCategory = async (
  categoryId: string,
): Promise<ApiResult<CatalogObject[]>> => {
  try {
    const response = await fetch(
      `/.netlify/functions/get-catalog-items-by-category?categoryId=${categoryId}`,
      {
        method: "GET",
        headers: { "Content-Type": "application/json", ...authHeaders },
      },
    );
    return response.json();
  } catch (error) {
    console.error("Error getting items by category:", error);
    return { error: `Error getting items by category: ${error}` };
  }
};

/* Free-text catalog search used to live here (searchItemsByTextQuery /
   searchItems, backed by the search-catalog-items function). Product search is
   now matched locally in `filterProductSearch` against the catalog the browser
   already holds — no round-trip per keystroke, and it inherits the
   catalog-visibility filtering `get-catalog` applies server-side, which the
   Square text search did not. */

/** Fetch all catalog item option definitions (colors, sizes, etc.). */
export const getOptions = async (): Promise<ApiResult<CatalogObject[]>> => {
  try {
    const response = await fetch(`/.netlify/functions/get-catalog-options`, {
      method: "GET",
      headers: { "Content-Type": "application/json", ...authHeaders },
    });
    return response.json();
  } catch (error) {
    console.error("Error getting catalog options:", error);
    return { error: `Error getting catalog options: ${error}` };
  }
};

/** Batch-fetch catalog objects by their IDs, with optional related objects. */
export const getObjects = async (
  objectIds: string[],
  includeRelatedObjects?: boolean,
): Promise<ApiResult<BatchRetrieveCatalogObjectsResponse>> => {
  try {
    const response = await fetch(`/.netlify/functions/get-catalog-objects`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders },
      body: JSON.stringify({ objectIds, includeRelatedObjects }),
    });
    return response.json();
  } catch (error) {
    console.error("Error getting catalog objects:", error);
    return { error: `Error getting catalog objects: ${error}` };
  }
};

// ---------------------------------------------------------------------------
// Smart fetch-or-cache helpers (used by CatalogContext)
// ---------------------------------------------------------------------------

/**
 * Fetches images for the given items if they aren't already in the local cache.
 * Uses the getImagesByIds accessor to check what's already available.
 */
export const findOrGetImages = async (
  items: CatalogObject[],
  addImages: (images: CatalogObject[]) => void,
  getImagesByIds: (ids: string[]) => CatalogObject[],
) => {
  // Collect all image IDs that aren't yet in the local cache.
  //
  // This is per-ID on purpose. It used to be all-or-nothing per item ("if I
  // hold NO image for this item, fetch them all"), which silently starved any
  // item whose image set was only PARTIALLY cached — and partial is the normal
  // case, for two reasons:
  //   1. Image objects are shared between products (one plain "back" photo of a
  //      given blank+colour serves every design printed on it — 8 images are
  //      shared across up to 5 items today). Browsing product A caches A's
  //      copies, so product B looked "already cached" and its OWN images — the
  //      fronts carrying the actual design — were never fetched.
  //   2. Adding a colour to an existing product appends images; a returning
  //      visitor holding the old ones never fetched the new colour's mockups.
  // The result was a product page rendering only plain garment backs, or no
  // mockup at all for a colour whose images were all unique — reported as
  // "images are backwards / the embroidery isn't visible" and "black and blue
  // show no mockup". Fetch exactly what's missing and the symptom can't recur.
  const cachedIds = new Set(
    getImagesByIds(
      Array.from(
        new Set(items.flatMap((item) => item.itemData?.imageIds ?? [])),
      ),
    ).map((image) => image.id),
  );
  const imageIds = Array.from(
    new Set(items.flatMap((item) => item.itemData?.imageIds ?? [])),
  ).filter((id) => !cachedIds.has(id));

  if (imageIds.length === 0) return;

  const imagesResponse = await getObjects(imageIds);
  if (imagesResponse?.data && !imagesResponse.error) {
    addImages(imagesResponse.data.objects ?? []);
  } else {
    console.error("Get Images failed:", imagesResponse?.error);
  }
};

/** Fetches items by IDs from remote if not already in the local cache, then fetches their images. */
export const findOrGetItemsByIds = async (
  itemIds: string[],
  addItems: (items: CatalogObject[]) => void,
  getItemsByIds: (ids: string[]) => CatalogObject[],
  addImages: (images: CatalogObject[]) => void,
  getImagesByIds: (ids: string[]) => CatalogObject[],
) => {
  let items = getItemsByIds(itemIds);
  if (items.length === 0) {
    const itemsResponse = await getObjects(itemIds);
    const itemsData = itemsResponse?.data;
    if (itemsData && !itemsResponse?.error) {
      items = itemsData.objects ?? [];
      addItems(itemsData.objects ?? []);
    } else {
      console.error("Get Items failed:", itemsResponse?.error);
      return;
    }
  }
  await findOrGetImages(items, addImages, getImagesByIds);
};

/** Fetches items for a category from remote if not already cached, then fetches their images. */
export const findOrGetItemsByCategory = async (
  categoryId: string,
  addItems: (items: CatalogObject[]) => void,
  getItemsByCategory: (categoryId: string) => CatalogObject[],
  addImages: (images: CatalogObject[]) => void,
  getImagesByIds: (ids: string[]) => CatalogObject[],
) => {
  let items = getItemsByCategory(categoryId);
  if (items.length === 0) {
    const itemsResponse = await getBaseItemsByCategory(categoryId);
    const itemsData = itemsResponse?.data;
    if (itemsData && !itemsResponse?.error) {
      items = itemsData;
      addItems(itemsData);
    } else {
      console.error("Get Items failed:", itemsResponse?.error);
      return;
    }
  }
  await findOrGetImages(items, addImages, getImagesByIds);
};

/**
 * Given a list of variation IDs, resolves their parent items and fetches images.
 * Returns the raw variation objects.
 */
export const findOrGetItemsByVariations = async (
  variationIds: string[],
  addItems: (items: CatalogObject[]) => void,
  addImages: (images: CatalogObject[]) => void,
  getImagesByIds: (ids: string[]) => CatalogObject[],
) => {
  try {
    const variationsResponse = await getObjects(variationIds, true);
    const variations = variationsResponse?.data;
    if (variations && !variationsResponse?.error) {
      const items = (variations.relatedObjects ?? []).filter(
        (v) => v.type === SquareFilterType.ITEM,
      );
      addItems(items);
      await findOrGetImages(items, addImages, getImagesByIds);
    } else {
      console.error("Get Variations failed:", variationsResponse?.error);
      return null;
    }
    return variations.objects;
  } catch (error) {
    console.error("Get Catalog Objects failed:", error);
    return null;
  }
};
